import moment from "moment";

import { formatDateTime } from "./preShipment";

/**
 * Battery usage admin page: the pure parts. The server works out the charge each
 * device has used from its telemetry (a "run" walks every device, a dry run only
 * records what it would write); this module turns runs and results into what the
 * page shows.
 */

/** The 2 x ER26500 pack, as the server's BatteryGauge has it. */
export const PACK_MAH = 18000;

export const RUN_RUNNING = "RUNNING";
export const RUN_COMPLETED = "COMPLETED";
export const RUN_CANCELLED = "CANCELLED";
export const RUN_FAILED = "FAILED";

/** How often a running run is polled for progress. */
export const POLL_MS = 5000;
export const RESULTS_PAGE_SIZE = 50;
/** The most the results endpoint hands back per request; the export pages through at this size. */
export const EXPORT_PAGE_SIZE = 1000;

export const DEFAULT_BATCH_SIZE = 25;
export const MAX_BATCH_SIZE = 200;
export const DEFAULT_PAUSE_MS = 500;

/** "1,234 mAh"; small figures keep a decimal so a few hours of sleep is not shown as 0. */
export function formatMah(value) {
  if (value === null || value === undefined || isNaN(value)) return "—";
  const rounded = Math.abs(value) < 100 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded.toLocaleString("en-US")} mAh`;
}

/** Share of the pack used, 0-100, one decimal. */
export function packPercentUsed(usedMah) {
  if (usedMah === null || usedMah === undefined) return null;
  const percent = (100 * usedMah) / PACK_MAH;
  return Math.round(Math.min(100, Math.max(0, percent)) * 10) / 10;
}

export function remainingMah(usedMah) {
  if (usedMah === null || usedMah === undefined) return null;
  return Math.max(0, PACK_MAH - usedMah);
}

const BREAKDOWN = [
  { key: "gnss", label: "GPS", field: "gnss_mah" },
  { key: "session", label: "Modem sessions", field: "session_mah" },
  { key: "sleep", label: "Sleep", field: "sleep_mah" },
  { key: "update", label: "Firmware updates", field: "update_mah" },
];

/** Where the charge went, biggest first, each with its share of the total. */
export function usageBreakdown(result) {
  if (!result) return [];
  const used = result.used_mah || 0;
  return BREAKDOWN.map((part) => {
    const mah = result[part.field] || 0;
    return { key: part.key, label: part.label, mah, share: used > 0 ? mah / used : 0 };
  }).sort((a, b) => b.mah - a.mah);
}

/**
 * "18 GPS searches, 2 without a record (10%)". A search is timed by the
 * GPS_LOCATION record it ends with; one without a record was cut short by the
 * next wake, or its record never came and it is charged its full timeout.
 */
export function gnssSummary(result) {
  if (!result) return "";
  const recorded = result.gnss_fixes || 0;
  const unrecorded = result.gnss_timeouts || 0;
  const searches = recorded + unrecorded;
  if (searches === 0) return "No GPS searches";
  const share = Math.round((100 * unrecorded) / searches);
  return `${pluralise(searches, "GPS search", "GPS searches")}, ${unrecorded} without a record (${share}%)`;
}

export function pluralise(count, noun, plural = `${noun}s`) {
  return `${count.toLocaleString("en-US")} ${count === 1 ? noun : plural}`;
}

/** Running but no longer moving: the worker is gone (an app restart) and the run can be resumed. */
export function runLabel(progress) {
  if (!progress || !progress.run) return "";
  if (progress.stale) return "Stalled";
  switch (progress.run.status) {
    case RUN_RUNNING:
      return "Running";
    case RUN_COMPLETED:
      return "Completed";
    case RUN_CANCELLED:
      return "Cancelled";
    case RUN_FAILED:
      return "Failed";
    default:
      return progress.run.status || "";
  }
}

/** Colour family for the status badge. */
export function runTone(progress) {
  if (!progress || !progress.run) return "muted";
  if (progress.stale) return "warn";
  switch (progress.run.status) {
    case RUN_RUNNING:
      return "active";
    case RUN_COMPLETED:
      return "done";
    case RUN_FAILED:
      return "failed";
    default:
      return "muted";
  }
}

/** Still moving, so worth polling. */
export function isRunActive(progress) {
  return !!progress && !!progress.run && progress.run.status === RUN_RUNNING && !progress.stale;
}

export function canCancel(progress) {
  return !!progress && !!progress.run && progress.run.status === RUN_RUNNING;
}

export function canResume(progress) {
  if (!progress || !progress.run) return false;
  const status = progress.run.status;
  return status === RUN_CANCELLED || status === RUN_FAILED || (status === RUN_RUNNING && !!progress.stale);
}

/** True when some run is still going - a new one cannot start until it ends or is cancelled. */
export function anyRunActive(runs) {
  return (runs || []).some(isRunActive);
}

export function runKind(run) {
  return run && run.dry_run ? "Dry run" : "Applied";
}

/** "about 4 min left". Empty when there is nothing to go on. */
export function formatEta(seconds) {
  if (seconds === null || seconds === undefined) return "";
  if (seconds < 60) return "under a minute left";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `about ${minutes} min left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `about ${hours} h${rest ? ` ${rest} min` : ""} left`;
}

/** The server keeps lists comma separated. */
export function splitList(value) {
  return (value || "")
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x);
}

/** "All device types" or "Impact Tracker V8, Impact Tracker V9". */
export function deviceTypesSummary(run, deviceTypeLabel) {
  const ids = splitList(run && run.devicetype_ids);
  if (ids.length === 0) return "All device types";
  return ids.map((id) => deviceTypeLabel(Number(id))).join(", ");
}

/** What a run covers: the devices picked for it, else its device types. */
export function runScope(run, deviceTypeLabel) {
  const ids = splitList(run && run.device_ids);
  if (ids.length === 1) return `Device #${ids[0]}`;
  if (ids.length > 1) return `${ids.length} picked devices`;
  return deviceTypesSummary(run, deviceTypeLabel);
}

/** True when the run was over the given device alone or among a few picked ones. */
export function runCoversDevice(run, deviceId) {
  return splitList(run && run.device_ids).includes(String(deviceId));
}

/** A run over one device: a single batch, no pause - it finishes in a moment. */
export function deviceRunOptions(deviceId, dryRun) {
  return { dry_run: !!dryRun, device_ids: [deviceId], batch_size: 1, pause_ms: 0 };
}

/** Batch size and pause as the form holds them (strings) turned into what the API takes; blank means default. */
export function runOptions({ dryRun, deviceTypes, batchSize, pauseMs }) {
  const toInt = (value) => {
    const n = parseInt(value, 10);
    return isNaN(n) ? undefined : n;
  };
  const batch = toInt(batchSize);
  const pause = toInt(pauseMs);
  return {
    dry_run: !!dryRun,
    devicetype_ids: deviceTypes && deviceTypes.length ? deviceTypes : undefined,
    batch_size: batch !== undefined ? Math.max(1, Math.min(MAX_BATCH_SIZE, batch)) : undefined,
    pause_ms: pause !== undefined ? Math.max(0, pause) : undefined,
  };
}

export const EXPORT_COLUMNS = [
  { header: "Device ID", width: 11, value: (r) => r.device_id },
  { header: "Serial", width: 16, value: (r) => (r.serial_number != null ? String(r.serial_number) : "") },
  { header: "Device type", width: 18, value: (r, ctx) => ctx.deviceTypeLabel(r.devicetype_id) },
  { header: "Firmware", width: 10, value: (r) => r.firmware },
  { header: "Legacy firmware", width: 15, value: (r) => (r.legacy_firmware ? "Yes" : "No") },
  { header: "Used mAh", width: 11, value: (r) => r.used_mah },
  { header: "Pack used %", width: 12, value: (r) => packPercentUsed(r.used_mah) },
  { header: "Sleep mAh", width: 11, value: (r) => r.sleep_mah },
  { header: "Sessions mAh", width: 13, value: (r) => r.session_mah },
  { header: "GPS mAh", width: 10, value: (r) => r.gnss_mah },
  { header: "Updates mAh", width: 12, value: (r) => r.update_mah },
  { header: "Records", width: 10, value: (r) => r.records },
  { header: "Wakes", width: 9, value: (r) => r.wakes },
  { header: "GPS searches recorded", width: 20, value: (r) => r.gnss_fixes },
  { header: "GPS searches without record", width: 25, value: (r) => r.gnss_timeouts },
  { header: "First telemetry", width: 22, value: (r) => formatDateTime(r.first_time) },
  { header: "Last telemetry", width: 22, value: (r) => formatDateTime(r.last_time) },
  { header: "Previous mAh", width: 13, value: (r) => r.previous_used_mah },
  { header: "Applied", width: 9, value: (r) => (r.applied ? "Yes" : "No") },
];

/** One object per result keyed by column header, the shape downloadWorkbook writes. */
export function toExportRows(results, ctx) {
  return (results || []).map((row) => {
    const out = {};
    EXPORT_COLUMNS.forEach((column) => {
      const value = column.value(row, ctx);
      out[column.header] = value === undefined ? null : value;
    });
    return out;
  });
}

export function exportFileName(run, date = new Date()) {
  const kind = run && run.dry_run ? "dry-run" : "applied";
  return `battery-usage_run-${run ? run.id : "x"}_${kind}_${moment(date).format("YYYY-MM-DD")}.xlsx`;
}
